import {PurchaseOrderItem, SourceMode, ToSourceItem} from "../../../../api/Logistics";
import {PhaseViewCol} from "../../../project/shopdrawing/openings/PhaseCol";
import {Column, NumberCol, StringCol, Table, ViewOnly} from "../../../../misc/scroller/Table";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {api} from "../../../../api/API";
import {ProjectCol, toSourceTableName} from "../PartsToSource";
import {CheckboxSelectorCol} from "../../../project/shopdrawing/Checkbox";
import {findValues} from "../FindValues";
import {DistributionMode} from "../DistributionModeToggle";
import {useActionCol} from "./ActionCol";
import {getAll} from "../../routes/useGetAll";
import {DateFilterCol} from "../../../../misc/DateFilterForCol";

export function Frame(props: {
    purchaseOrder?: number;
    configureSourceGroup?: number;
    supplier?: {
        id: number;
        name: string;
    }
    distributionMode: DistributionMode
    sourceMode: SourceMode
    project?: number;
}) {
    const usr = useUser();

    const isRestock = props.sourceMode === "stock"

    const actionCol = useActionCol({
        purchaseOrder: props.purchaseOrder,
        configureSourceGroup: props.configureSourceGroup,
        sourceMode: props.sourceMode,
        update: (input: Partial<PurchaseOrderItem>, dt: ToSourceItem) => {
            const inventoryReqs = dt.inventoryRequestId ? [dt.inventoryRequestId] : [];
            if(dt.frameAnchorDetail) {
                inventoryReqs.push(...findValues(dt.frameAnchorDetail, d => d.inventoryRequestId));
            }

            return Object.assign({}, input, {
                kind: "frame",
                inventoryRequests: inventoryReqs,
            });
        }
    });

    return (
        <Table<ToSourceItem>
            name={toSourceTableName}
            overridePrefsName={toSourceTableName + ".frame." + props.sourceMode}
            columns={[
                (props.purchaseOrder || props.configureSourceGroup) ? CheckboxSelectorCol({
                    disabled: row => !hasPermission(usr, "CanReleaseAndPurchase") && usr?.id === row.releasedBy,
                }) : null,
                !isRestock && ViewOnly(ProjectCol("Project", "projectName", "projectNumber", 100)),
                !isRestock && ViewOnly(StringCol("Opening", "openingName", 100)),
                !isRestock && ViewOnly(StringCol("Floor", "openingFloor", 70)),
                !isRestock && PhaseViewCol<ToSourceItem>("openingPhase", 70),
                DateFilterCol<ToSourceItem>("Due", "neededBy", "MMM D, YYYY", 100, "future"),
                ViewOnly({
                    name: "Series",
                    width: 100,
                    render: (dt: ToSourceItem, col: Column<ToSourceItem>) => {
                        return dt.kind === "frame" ? dt.frameSeries : `Anchor: ${dt.productCode}`
                    }
                }),
                ViewOnly(StringCol("Material", "frameMaterial", 70)),
                ViewOnly(StringCol("Gauge", "frameGauge", 70)),
                ViewOnly(StringCol("Screen Elev", "frameScreenElev", 100)),
                ViewOnly(StringCol("Profile", "frameProfile", 70)),
                ViewOnly(StringCol("Jamb Depth", "frameJambDepth", 100)),
                ViewOnly(StringCol("Door Thickness", "frameDoorThickness", 100)),
                ViewOnly(StringCol("Construction", "frameConstruction", 100)),
                ViewOnly(StringCol("Handing", "frameHanding", 70)),
                ViewOnly(StringCol("Hardware Preps", "frameHardwarePreps", 130)),
                ViewOnly(StringCol("Label", "frameLabel", 100)),
                ViewOnly(StringCol("Type", "frameType", 70)),
                ViewOnly(StringCol("Nom. Width", "dimWidth", 70)),
                ViewOnly(StringCol("Nom. Height", "dimHeight", 70)),
                ViewOnly(NumberCol("Need", "qty", 60)),
                isRestock && ViewOnly(NumberCol("Stock", "stockQty", 60)),
                isRestock && ViewOnly(NumberCol("Rsrvd", "reservedQty", 60)),
                isRestock && ViewOnly(NumberCol("Pend.PO", "purchasedQty", 90)),
                isRestock && ViewOnly(NumberCol("Stock Min", "stockMinQty", 90)),
                isRestock && ViewOnly(NumberCol("Ideal Qty", "idealQty", 90)),
                actionCol,
            ]}
            fetch={async (ctx) => {
                const list = await getAll(ctx, offset => api.logistics.listToSourceItems({
                    offset,
                    supplier: props.supplier?.id,
                    type: "frame",
                    sourceMode: props.sourceMode,
                    distributionMode: props.distributionMode,
                    project: props.project,
                }))

                // remove frame anchors from the list and nest them in the frame they belong to
                return list.filter(l => {
                    if(l.kind === "frame-anchor" && l.partOfFrame && l.openingId) {
                        const frame = list.find(s => s.openingId === l.openingId && s.kind === "frame")
                        if(!frame) {
                            console.warn(l);
                            return true;
                        }

                        frame.frameAnchorName = l.productCode;

                        // push items b/c there may be 2 anchor groups for pair door frames.
                        if(!frame.frameAnchorDetail) frame.frameAnchorDetail = [];
                        frame.frameAnchorDetail.push(l);
                        return false;
                    }

                    return true;
                })
            }}
            fetchDeps={[props.supplier, props.sourceMode, props.distributionMode, props.project]}
        />
    )
}